'use client';

import React, { useMemo, useState } from 'react';
import { faFileInvoiceDollar } from '@fortawesome/pro-solid-svg-icons';
import {
  FilterIcon,
  IncludeExcludeSelect,
  FilterAutocomplete,
  Divider,
  FilterLabel,
  ClearFilterButton,
} from 'src/components/FilterSets/components';
import { SelectedFilters } from 'src/components/FilterSets/SelectedFilters';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useGetPayerNameSuggestionsInfiniteQuery } from 'src/api/redux';
import type { FilterValue } from 'src/context/reducers/filterSetReducer';

/**
 * Payer Name filter (Territory Analytics only).
 *
 * Selected values are Payer entity ids; the backend resolves them to the payer
 * names the claims rows carry. PAYER_NAMES is a synthetic filter — it is sent on
 * a request but never saved to a filter set (see PARENT_FILTER_MAPPING in
 * src/hooks/filters/types.ts).
 */
const PayerName = ({
  location,
  smallLabel,
}: {
  location: string;
  smallLabel?: boolean;
}) => {
  const { filterSet, updateFilter, removeFilterValue } = useFilterSets(location);
  const filterValue = filterSet.payerNames;
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useGetPayerNameSuggestionsInfiniteQuery({
      q: searchTerm,
    });

  const options = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) =>
      (page.results ?? []).map((result) => ({
        value: result.id,
        label: result.name,
      }))
    );
  }, [data]);

  const handleFilterChange = (value: FilterValue) => {
    updateFilter('payerNames', value);
  };

  const handleRemoveFilter = (filterLabel: string) => {
    removeFilterValue('payerNames', filterLabel);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const hasValues = filterValue?.values?.length;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <FilterLabel smallLabel={smallLabel}>Payer Name</FilterLabel>
        <ClearFilterButton
          type="payerNames"
          isDisabled={!hasValues}
          location={location}
        />
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faFileInvoiceDollar} />
          <IncludeExcludeSelect
            title="Payer Name"
            value={filterValue}
            onChange={handleFilterChange}
          />
          <Divider />
          <FilterAutocomplete
            options={options}
            value={filterValue}
            onChange={handleFilterChange}
            isLoading={isLoading}
            placeholder="Search payer name..."
            onSearch={handleSearch}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetching}
            fetchNextPage={fetchNextPage}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filterValue?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filterValue?.is_all}
      />
    </div>
  );
};

export default PayerName;
